/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { ToastType } from '@components';
import mapConfig from '@components/Map/mapConfig';
import { getDB } from '@database';
import { addApiCall } from '@store/api/store';
import { ConfigManager } from '@utils/ConfigManager';
import {
  deepConvertCamelToPascalCase,
  deepConvertPascalToCamelCase,
} from '@utils/caseTransformers';
import { logError } from '@utils/errorLogging';
import { routeLoginPage } from '@utils/routeLoginPage';
import { handleToast, printLog } from '@utils/utils';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  ApiErrorT,
  ApiResponseT,
  ErrorResponseT,
  ErrorT,
  ProblemDetailsT,
} from './types';

export type ApiResponse<T> = {
  data: T;
  error?: {
    code: string;
    message: string;
  };
  errors?: {
    code: string;
    message: string;
  }[];
  isFailure: boolean;
  isSuccess: boolean;
  value: T;
};

const TIMEOUT_DURATION = 60_000;
const ACCESS_TOKEN_STORAGE_KEY = 'atg-access-token';

const getAccessToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
};

const apiClient: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.apiUrl}/api/`, // Set your API base URL
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies in the request
});
const apiPdfClient: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.pdfApiUrl}/api/`,
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies in the request
});

const apiClientFormData: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.apiUrl}/api/`, // Set your API base URL
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  withCredentials: true, // Enable sending cookies in the request
});

const v3Client: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.yarpHost}/api/`, // Set your API base URL
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies in the request
});

const v3ODataClient: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.yarpHost}/odata/`, // Set your API base URL
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies in the request
});

const pcMilerClient: AxiosInstance = axios.create({
  baseURL: mapConfig.PCMiler.PCMilerBaseUrl,
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'application/json',
    Authorization: mapConfig.PCMiler.PCMilerAPIKey,
  },
});

const mapClient: AxiosInstance = axios.create({
  baseURL: mapConfig.PCMiler.PCMilerBaseUrl,
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'application/json',
    Authorization: mapConfig.PCMiler.PCMilerAPIKey,
  },
  withCredentials: false,
});

const v3FileUploadClient: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.yarpHost}/api/`,
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  withCredentials: true,
});

const apiFileUploadClient: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.apiUrl}/api/`,
  timeout: 30000,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  withCredentials: true,
});

const falveyClient: AxiosInstance = axios.create({
  baseURL: `${ConfigManager.falveyUrl.replace(/\/$/, '')}/api/`, // Set your API base URL
  timeout: TIMEOUT_DURATION,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies in the request
});

const isAxiosDebugActive = ConfigManager.isDebugging === 'true';

export function authorizationInterceptor(config: InternalAxiosRequestConfig) {
  const token = getAccessToken();
  const isLoginRequest = Boolean(config.url?.includes('/login'));
  const existingAuth = config.headers.get('Authorization');
  const hasApplicationToken =
    typeof existingAuth === 'string' &&
    existingAuth.startsWith('ApplicationToken');

  if (
    token !== null &&
    token.length > 0 &&
    !isLoginRequest &&
    !hasApplicationToken
  ) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  if (isAxiosDebugActive) {
    printLog('Request:', config);
  }
  return config;
}

/** Logs request errors to Datadog as `errorName`, or only to the console when omitted. */
const createRequestErrorHandler =
  (errorName?: string) =>
  (error: AxiosError): Promise<never> => {
    if (errorName) {
      logError(errorName, error);
    } else {
      printLog('axios error', error);
    }
    if (isAxiosDebugActive) {
      printLog(error);
    }
    return Promise.reject(error);
  };

/** v3 and PC*MILER APIs expect PascalCase request bodies. */
const pascalCaseRequestData = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => ({
  ...config,
  data: deepConvertCamelToPascalCase(config.data),
});

const trackApiCall = (
  url: string | undefined,
  method: string | undefined,
  status: number,
) => {
  addApiCall({ url, method, status, timeStamp: new Date().toISOString() });
};

const trackFailedCall = (
  error: AxiosError,
  useRequestUrl = false,
  strict = false,
) => {
  const url = useRequestUrl
    ? error?.config?.url
    : (
        (strict ? error.request : error?.request) as
          | { responseURL?: string }
          | undefined
      )?.responseURL;

  trackApiCall(
    url ?? 'n/a',
    error?.config?.method ?? 'n/a',
    error?.response?.status ?? 0,
  );
};

const expireSession = () => {
  logError('sessionTimeOut', new Error('Session timeout'));
  routeLoginPage();
};

const clearCache = () => {
  getDB()?.tables.forEach((table) => table.clear());
};

const toNoContentResponse = (response: AxiosResponse) =>
  ({
    data: {
      isFailure: false,
      isSuccess: true,
      value: null,
      data: null,
    },
    status: 204,
    statusText: response.statusText,
    headers: response.headers,
    config: response.config,
  }) as AxiosResponse<ApiResponse<unknown>>;

const logUnsuccessfulResponse = (
  response: AxiosResponse,
  toastServerError: boolean,
) => {
  const { status, config, data } = response;

  let errorName: string | undefined;
  if (status >= 300 && status < 400) {
    errorName = 'apiRedirectionError';
  } else if (status >= 400 && status < 500) {
    errorName = 'apiClientError';
  } else if (status >= 500) {
    if (toastServerError) {
      handleToast(
        ToastType.Error,
        'There was a cool error here that is a server error',
      );
    }
    errorName = 'apiServerError';
  }

  if (errorName) {
    logError(
      errorName,
      new Error(
        `${config.method?.toUpperCase() ?? 'n/a'} ${config.url ?? 'n/a'} responded with ${status}`,
      ),
      { responseData: data },
    );
  }
};

/** Reduces a failed V4 request to `ErrorT[]` (docs/instructions/v4-error-handling.md). */
const toErrorMessages = (error: AxiosError): ErrorT[] => {
  const errorResponse = error as unknown as {
    response: ErrorResponseT;
  };

  let errorMessages: ErrorT[] = [];

  if (
    !!errorResponse.response &&
    typeof errorResponse.response.data === 'string'
  ) {
    errorMessages = [{ errorMessage: errorResponse.response.data }];
  } else if (
    !!errorResponse.response &&
    typeof errorResponse.response.data !== 'string' &&
    'errors' in errorResponse.response.data &&
    Boolean(errorResponse.response.data.errors)
  ) {
    const errors = errorResponse.response.data.errors;

    if ('map' in errors) {
      errorMessages = errors as ErrorT[];
    } else {
      // If the errors field is an object, its keys will represent the section of the POST/PUT
      // data that failed) with an array of string messages. So we create an error object for
      // each of these messages and reduce the bigger object into an array of errors.
      errorMessages = Object.keys(errors).reduce((acc, key) => {
        errors[key].forEach((err) => {
          acc.push({
            propertyName: key,
            errorMessage: err,
          });
        });

        return acc;
      }, [] as ErrorT[]);
    }
  } else if (
    !!errorResponse.response &&
    errorResponse.response.data instanceof Array
  ) {
    errorMessages = errorResponse.response.data.map((err) => {
      return {
        errorMessage:
          'message' in err
            ? (err.message ?? 'Unknown error')
            : err.errorMessage,
      };
    });
  } else if (error.response?.status !== undefined) {
    if (Boolean((errorResponse.response.data as ProblemDetailsT).detail)) {
      errorMessages.push({
        errorMessage: (errorResponse.response.data as ProblemDetailsT).detail,
      });
    } else {
      errorMessages.push({
        errorMessage:
          (errorResponse.response.data as ErrorT).message ?? 'Unknown error',
      });
    }
  }

  return errorMessages;
};

const rejectV4Error = (error: AxiosError): Promise<never> => {
  if (error.response?.status === 401) {
    expireSession();
    clearCache();
    return Promise.reject([{ errorMessage: 'You have been logged out' }]);
  }

  trackFailedCall(error);
  return Promise.reject(toErrorMessages(error));
};

const getFalveyErrorMessage = (error: AxiosError) => {
  const errorResponse = error as unknown as {
    response: { data: { errors: [{ errorMessage: string }] } };
  };

  if (Boolean(errorResponse.response.data.errors)) {
    return errorResponse.response.data.errors
      .map((errorObj) => errorObj.errorMessage)
      .join(' , ');
  }

  const tempError = error as unknown as
    | ApiResponseT<ApiErrorT[]>
    | ApiResponseT<ApiErrorT>;
  let errorMessages: string[] = [];

  if (tempError.response.data instanceof Array) {
    errorMessages = tempError.response.data.map(
      (errorObj) => errorObj.message ?? 'Unknown error',
    );
  } else if (error.response?.status !== undefined) {
    errorMessages.push(tempError.response.data.message ?? 'Unknown error');
  }

  return errorMessages.join(' , ');
};

const getV3ErrorMessage = (error: AxiosError) => {
  const errorResponse = error as unknown as {
    response: { data: { Message: string; Messages: string[] } };
  };

  return (
    errorResponse.response.data.Message ||
    errorResponse.response.data.Messages.join(' , ') ||
    'Something went wrong'
  );
};

const getV3ODataErrorMessage = (error: AxiosError) =>
  (
    error as unknown as {
      response: { data: { error: { message: string } } };
    }
  ).response.data.error.message || 'Something went wrong';

const getV3FileUploadErrorMessage = (error: AxiosError) =>
  (error as unknown as { response: { data: { Message: string } } }).response
    .data.Message;

type ResponseInterceptorOptions = {
  /** Reshapes 2xx payloads, e.g. PascalCase APIs to camelCase. */
  transformData?: (data: unknown) => unknown;
  /** Resolve 204 with a success envelope instead of the empty body. */
  wrapNoContent?: boolean;
  /** V4 contract: failures reject with `ErrorT[]` and a 401 expires the session. */
  v4ErrorHandling?: boolean;
  /** Log and reject non-2xx responses accepted by a custom `validateStatus`; defaults to `v4ErrorHandling`. */
  rejectUnsuccessful?: boolean;
  /** Redirect to login on 401; `'accepted'` only covers responses accepted by `validateStatus`. */
  loginOnUnauthorized?: 'accepted' | 'always';
  /** Message to toast when a request fails. */
  getErrorToast?: (error: AxiosError) => string;
  /** Clear the local DB when a request fails. */
  clearCacheOnError?: boolean;
  /** Track failed calls by `config.url` instead of the final response URL. */
  trackRequestUrl?: boolean;
  /** Read the rejection without null checks, so a null/undefined rejection throws. */
  strictTracking?: boolean;
};

/** Builds the `[onFulfilled, onRejected]` pair each client attaches with `interceptors.response.use`. */
const createResponseInterceptor = ({
  transformData,
  wrapNoContent = false,
  v4ErrorHandling = false,
  rejectUnsuccessful = v4ErrorHandling,
  loginOnUnauthorized,
  getErrorToast,
  clearCacheOnError = false,
  trackRequestUrl = false,
  strictTracking = false,
}: ResponseInterceptorOptions) => {
  const onFulfilled = (response: AxiosResponse): AxiosResponse => {
    const { status, config } = response;
    trackApiCall(config.url, config.method, status);

    if (status >= 200 && status < 300) {
      if (wrapNoContent && status === 204) {
        return toNoContentResponse(response);
      }
      return (
        transformData ? transformData(response.data) : response.data
      ) as AxiosResponse;
    }

    // Non-2xx statuses only get here when a custom `validateStatus` accepts them.
    if (status === 401) {
      if (v4ErrorHandling) {
        expireSession();
      } else if (loginOnUnauthorized) {
        routeLoginPage();
      }
    } else if (rejectUnsuccessful) {
      logUnsuccessfulResponse(response, v4ErrorHandling);
    }

    if (rejectUnsuccessful) {
      throw response;
    }
    return response;
  };

  const onRejected = (error: AxiosError): Promise<never> | undefined => {
    if (v4ErrorHandling) {
      return rejectV4Error(error);
    }

    trackFailedCall(error, trackRequestUrl, strictTracking);
    // Legacy falvey/v3 behavior: non-Error rejections resolve with undefined.
    if (getErrorToast && !(error instanceof Error)) {
      return undefined;
    }
    if (loginOnUnauthorized === 'always' && error.response?.status === 401) {
      routeLoginPage();
    }
    if (clearCacheOnError) {
      clearCache();
    }
    if (getErrorToast) {
      handleToast(ToastType.Error, getErrorToast(error));
    }
    return Promise.reject(error);
  };

  return [onFulfilled, onRejected] as const;
};

const v3ResponseOptions: ResponseInterceptorOptions = {
  transformData: deepConvertPascalToCamelCase,
  loginOnUnauthorized: 'accepted',
  trackRequestUrl: true,
};

apiClient.interceptors.request.use(
  authorizationInterceptor,
  createRequestErrorHandler(),
);
apiClient.interceptors.response.use(
  ...createResponseInterceptor({ v4ErrorHandling: true, wrapNoContent: true }),
);

apiPdfClient.interceptors.request.use(
  authorizationInterceptor,
  createRequestErrorHandler(),
);
apiPdfClient.interceptors.response.use(
  ...createResponseInterceptor({ v4ErrorHandling: true, wrapNoContent: true }),
);

apiFileUploadClient.interceptors.request.use(
  authorizationInterceptor,
  createRequestErrorHandler(),
);
apiFileUploadClient.interceptors.response.use(
  ...createResponseInterceptor({ v4ErrorHandling: true }),
);

apiClientFormData.interceptors.request.use(
  authorizationInterceptor,
  createRequestErrorHandler(),
);

falveyClient.interceptors.request.use(
  authorizationInterceptor,
  createRequestErrorHandler('falveyClientRequestError'),
);
falveyClient.interceptors.response.use(
  ...createResponseInterceptor({
    rejectUnsuccessful: true,
    loginOnUnauthorized: 'always',
    getErrorToast: getFalveyErrorMessage,
    strictTracking: true,
  }),
);

v3Client.interceptors.request.use(pascalCaseRequestData);
v3Client.interceptors.response.use(
  ...createResponseInterceptor({
    ...v3ResponseOptions,
    // v3 failures clear cached data until the v4 pending approvals API is integrated.
    clearCacheOnError: true,
    getErrorToast: getV3ErrorMessage,
  }),
);

v3ODataClient.interceptors.request.use(pascalCaseRequestData);
v3ODataClient.interceptors.response.use(
  ...createResponseInterceptor({
    ...v3ResponseOptions,
    getErrorToast: getV3ODataErrorMessage,
  }),
);

v3FileUploadClient.interceptors.request.use(
  (config) => config,
  createRequestErrorHandler('v3FileUploadClientRequestError'),
);
v3FileUploadClient.interceptors.response.use(
  ...createResponseInterceptor({
    ...v3ResponseOptions,
    getErrorToast: getV3FileUploadErrorMessage,
  }),
);

pcMilerClient.interceptors.request.use(pascalCaseRequestData);
pcMilerClient.interceptors.response.use(
  ...createResponseInterceptor({ transformData: deepConvertPascalToCamelCase }),
);

export {
  apiClient,
  apiClientFormData,
  apiFileUploadClient,
  apiPdfClient,
  falveyClient,
  mapClient,
  pcMilerClient,
  v3Client,
  v3FileUploadClient,
  v3ODataClient,
};
